import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChatCircleDots,
  X,
  ArrowLeft,
  ArrowRight,
  House,
  Warning,
  PaperPlaneRight,
  HandHeart,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { FLOW, HUMAN_CHIPS, routeFreeText } from "./flow";
import { isCrisisMessage } from "./crisis";
import { analytics } from "./analytics";
import WarmWelcome, { suppressWarmWelcome } from "./WarmWelcome";

// Routes where the guide should NOT appear.
const HIDDEN_ROUTES = ["/admin", "/login", "/unsubscribed", "/donate/success"];

// Known application routes. Anything outside this list is treated as a 404
// (React Router's '*' catch-all) and the guide is hidden.
const KNOWN_ROUTES = [
  "/",
  "/about",
  "/grief-support",
  "/books",
  "/classes",
  "/request-support",
  "/donate",
  "/donate/success",
  "/contact",
  "/ask-chaplain-derrick",
  "/from-diagnosis-to-goodbye",
  "/resources",
  "/bulk-orders",
  "/organization-bookings",
  "/unsubscribed",
  "/login",
  "/admin",
];

// View states inside the panel.
const VIEW = {
  MENU: "menu",
  FREE_TEXT: "free-text",
  CRISIS_INTRO: "crisis-intro",
  CRISIS_US: "crisis-us",
  CRISIS_INTL: "crisis-intl",
};

const MAX_FREE_TEXT = 300;

const Chip = ({ children, onClick, "data-testid": testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-sm"
  >
    {children}
  </button>
);

const OptionButton = ({ label, onClick, "data-testid": testId, external }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex w-full items-center justify-between gap-3 rounded-xl border border-ink-100 bg-white px-4 py-3 text-left text-sm font-medium text-ink-800 shadow-[0_1px_0_rgba(15,23,42,0.02)] transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:bg-softblue/40 hover:text-primary"
  >
    <span className="pr-2">{label}</span>
    {external ? (
      <ArrowUpRight
        size={16}
        weight="bold"
        className="shrink-0 text-ink-400 transition-colors group-hover:text-primary"
      />
    ) : (
      <ArrowRight
        size={16}
        weight="bold"
        className="shrink-0 text-ink-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
      />
    )}
  </button>
);

const BeginAgainGuide = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(VIEW.MENU);
  const [nodeId, setNodeId] = useState("main");
  const [history, setHistory] = useState([]); // stack of previous node ids
  const [freeText, setFreeText] = useState("");
  const [freeTextError, setFreeTextError] = useState("");
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);
  const engagedFiredRef = useRef(false);

  // Primary pathway ids offered from the main menu (used to distinguish
  // top-level pathway selects from sub-menu navigation).
  const PRIMARY_PATHWAYS = useMemo(
    () => new Set(["grief", "caregiving", "hospice", "stress", "prayer", "books", "workshops", "ask", "contact", "unsure"]),
    []
  );

  const markEngaged = useCallback(() => {
    if (engagedFiredRef.current) return;
    engagedFiredRef.current = true;
    analytics.engaged();
  }, []);

  // Hide on system/private routes AND on unknown paths (which resolve to the
  // 404 NotFound page via React Router's '*' catch-all).
  const hidden = useMemo(() => {
    if (HIDDEN_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"))) {
      return true;
    }
    const isKnown =
      KNOWN_ROUTES.includes(pathname) ||
      KNOWN_ROUTES.some((r) => r !== "/" && pathname.startsWith(r + "/"));
    return !isKnown;
  }, [pathname]);

  // Reset conversation on close so a re-open starts fresh at main menu.
  const resetToMenu = useCallback(() => {
    setNodeId("main");
    setHistory([]);
    setView(VIEW.MENU);
    setFreeText("");
    setFreeTextError("");
  }, []);

  // Fire analytics.open() once per open cycle.
  const openPanel = useCallback(() => {
    setOpen(true);
    engagedFiredRef.current = false;
    // Any Guide open (voluntary from FAB or via Warm Welcome) suppresses the
    // Warm Welcome for the rest of this session.
    suppressWarmWelcome();
    analytics.open();
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    // Delay reset so the close animation shows the same content.
    setTimeout(resetToMenu, 250);
  }, [resetToMenu]);

  // ESC to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closePanel]);

  // Focus the close button when the panel opens.
  useEffect(() => {
    if (open && closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
  }, [open]);

  // If we navigate away (option with to:) close the widget so the user reaches the page.
  const goToPath = useCallback(
    (to) => {
      analytics.routeClick(nodeId, to);
      markEngaged();
      closePanel();
      // slight delay so close animation isn't jarring
      setTimeout(() => navigate(to), 120);
    },
    [closePanel, navigate, nodeId, markEngaged]
  );

  const pushNode = useCallback(
    (nextId) => {
      setHistory((h) => [...h, nodeId]);
      setNodeId(nextId);
      setView(VIEW.MENU);
    },
    [nodeId]
  );

  const goBack = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setNodeId(prev);
      setView(VIEW.MENU);
      return h.slice(0, -1);
    });
  }, []);

  const currentNode = FLOW[nodeId] || FLOW.main;

  const handleOption = useCallback(
    (opt) => {
      if (opt.action === "restart") {
        resetToMenu();
        return;
      }
      if (opt.action === "free-text") {
        setView(VIEW.FREE_TEXT);
        return;
      }
      if (opt.action === "crisis") {
        analytics.safetyTriggered();
        setView(VIEW.CRISIS_INTRO);
        return;
      }
      if (opt.goto) {
        if (PRIMARY_PATHWAYS.has(opt.goto)) {
          analytics.primaryPathway(opt.goto);
          markEngaged();
        }
        pushNode(opt.goto);
        return;
      }
      if (opt.to) {
        goToPath(opt.to);
        return;
      }
      if (opt.href) {
        analytics.externalClick(nodeId, opt.href);
        markEngaged();
        window.open(opt.href, "_blank", "noopener,noreferrer");
      }
    },
    [resetToMenu, pushNode, goToPath, PRIMARY_PATHWAYS, markEngaged, nodeId]
  );

  const submitFreeText = useCallback(() => {
    const text = freeText.trim();
    if (!text) {
      setFreeTextError("Please share a few words about what you're looking for.");
      return;
    }
    if (isCrisisMessage(text)) {
      analytics.safetyTriggered();
      setView(VIEW.CRISIS_INTRO);
      return;
    }
    const target = routeFreeText(text) || "unsure";
    analytics.freeTextSubmit(target);
    markEngaged();
    pushNode(target);
    setFreeText("");
    setFreeTextError("");
  }, [freeText, pushNode, markEngaged]);

  if (hidden) return null;

  return (
    <>
      {/* Warm Welcome invitation (renders only when panel is closed and its
          own eligibility/session rules allow it) */}
      {!open && <WarmWelcome onOpenGuide={openPanel} />}

      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={openPanel}
          data-testid="begin-again-guide-toggle"
          aria-label="Open Begin Again Guide"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3.5 text-sm font-semibold text-white shadow-[0_18px_40px_-12px_rgba(91,122,148,0.55)] transition-all hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-[0_22px_50px_-12px_rgba(91,122,148,0.7)] sm:bottom-6 sm:right-6"
        >
          <ChatCircleDots size={20} weight="duotone" />
          <span className="hidden sm:inline">Begin Again Guide</span>
          <span className="sm:hidden">Guide</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-0 z-40 sm:inset-auto sm:bottom-6 sm:right-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="begin-again-guide-title"
        >
          {/* Mobile backdrop (below sm only) */}
          <button
            type="button"
            aria-label="Close Begin Again Guide"
            onClick={closePanel}
            className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm sm:hidden"
            data-testid="begin-again-guide-backdrop"
          />

          <div
            ref={panelRef}
            data-testid="begin-again-guide-panel"
            className="absolute inset-x-0 bottom-0 flex max-h-[92vh] flex-col overflow-hidden rounded-t-2xl border border-ink-100 bg-white shadow-[0_-20px_60px_-15px_rgba(15,23,42,0.35)] sm:static sm:h-[min(640px,80vh)] sm:w-[380px] sm:max-h-none sm:rounded-2xl sm:shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)]"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-ink-100 bg-gradient-to-br from-primary to-primary-dark px-4 py-3 text-white">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
                <HandHeart size={18} weight="duotone" />
              </span>
              <div className="min-w-0 flex-1">
                <div
                  id="begin-again-guide-title"
                  className="text-[10px] font-semibold uppercase tracking-[0.22em] text-secondary"
                >
                  Begin Again Living Ministries
                </div>
                <div className="truncate font-heading text-base font-medium leading-tight">
                  Begin Again Guide
                </div>
              </div>
              {(history.length > 0 || view !== VIEW.MENU) && (
                <button
                  type="button"
                  onClick={() => {
                    if (view !== VIEW.MENU) {
                      setView(VIEW.MENU);
                    } else {
                      goBack();
                    }
                  }}
                  data-testid="begin-again-guide-back"
                  aria-label="Go back"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <ArrowLeft size={16} weight="bold" />
                </button>
              )}
              <button
                type="button"
                onClick={resetToMenu}
                data-testid="begin-again-guide-restart"
                aria-label="Return to main menu"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
              >
                <House size={16} weight="bold" />
              </button>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={closePanel}
                data-testid="begin-again-guide-close"
                aria-label="Close Begin Again Guide"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto bg-softblue/30 px-4 py-4">
              {view === VIEW.MENU && (
                <MenuView
                  node={currentNode}
                  onOption={handleOption}
                  onCrisis={() => {
                    analytics.safetyTriggered();
                    setView(VIEW.CRISIS_INTRO);
                  }}
                />
              )}
              {view === VIEW.FREE_TEXT && (
                <FreeTextView
                  value={freeText}
                  onChange={(v) => {
                    setFreeText(v.slice(0, MAX_FREE_TEXT));
                    if (freeTextError) setFreeTextError("");
                  }}
                  onSubmit={submitFreeText}
                  error={freeTextError}
                />
              )}
              {view === VIEW.CRISIS_INTRO && (
                <CrisisIntroView
                  onUS={() => {
                    analytics.safetyCountry("us");
                    setView(VIEW.CRISIS_US);
                  }}
                  onIntl={() => {
                    analytics.safetyCountry("intl");
                    setView(VIEW.CRISIS_INTL);
                  }}
                />
              )}
              {view === VIEW.CRISIS_US && <CrisisUSView />}
              {view === VIEW.CRISIS_INTL && <CrisisIntlView />}

              {/* Human-connect chips (hidden on crisis and free-text screens) */}
              {view === VIEW.MENU && nodeId !== "main" && (
                <div className="mt-6 rounded-xl border border-ink-100 bg-white/70 p-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                    Connect with a person
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {HUMAN_CHIPS.map((chip) => (
                      <Chip
                        key={chip.label}
                        onClick={() => {
                          analytics.humanChip(chip.id);
                          goToPath(chip.to);
                        }}
                        data-testid={`bag-human-${chip.id}`}
                      >
                        {chip.label}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer disclaimer */}
            <div className="border-t border-ink-100 bg-white px-4 py-2.5">
              <p className="text-[11px] leading-snug text-ink-500">
                Begin Again Living Ministries provides educational, spiritual,
                and supportive resources. This assistant does not provide
                medical, mental-health, legal, or emergency services.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// -------------------- Sub views --------------------

const MenuView = ({ node, onOption, onCrisis }) => (
  <div data-testid={`bag-node-${node.title ? node.title.toLowerCase().replace(/[^a-z]+/g, "-") : "main"}`}>
    {node.title && (
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-primary">
        {node.title}
      </div>
    )}
    <p className="mb-4 text-sm leading-relaxed text-ink-700">{node.prompt}</p>
    <div className="flex flex-col gap-2">
      {node.options.map((opt) => (
        <OptionButton
          key={opt.label}
          label={opt.label}
          external={Boolean(opt.href)}
          onClick={() => onOption(opt)}
          data-testid={`bag-option-${opt.label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "")}`}
        />
      ))}
    </div>
    {/* Subtle safety link, always visible in menus (except when already showing crisis) */}
    <button
      type="button"
      onClick={onCrisis}
      data-testid="bag-crisis-link"
      className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500 underline decoration-ink-300 underline-offset-4 hover:text-primary hover:decoration-primary"
    >
      <Warning size={12} weight="bold" />
      In immediate danger? Get emergency help
    </button>
  </div>
);

const FreeTextView = ({ value, onChange, onSubmit, error }) => (
  <div data-testid="bag-free-text-view">
    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-primary">
      Type your concern
    </div>
    <p className="mb-3 text-sm leading-relaxed text-ink-700">
      Share a few words about what you&rsquo;re looking for. This is a simple guide &mdash;
      we&rsquo;ll help route you to the right resource or a real person on our team.
    </p>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onSubmit();
        }
      }}
      placeholder="e.g. My mom just entered hospice and I don't know what to do"
      rows={4}
      maxLength={MAX_FREE_TEXT}
      data-testid="bag-free-text-input"
      className="block w-full resize-none rounded-xl border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-800 shadow-inner placeholder:text-ink-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    />
    <div className="mt-1 flex items-center justify-between">
      <span className="text-[11px] text-ink-500">
        {value.length}/{MAX_FREE_TEXT}
      </span>
      <button
        type="button"
        onClick={onSubmit}
        data-testid="bag-free-text-submit"
        className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-dark"
      >
        <PaperPlaneRight size={14} weight="bold" />
        Find help
      </button>
    </div>
    {error && (
      <p
        role="alert"
        data-testid="bag-free-text-error"
        className="mt-2 text-xs text-red-600"
      >
        {error}
      </p>
    )}
    <p className="mt-4 text-[11px] leading-snug text-ink-500">
      Please do not enter sensitive medical or personal details. For a private
      prayer or support request, use the secure request form.
    </p>
  </div>
);

const CrisisIntroView = ({ onUS, onIntl }) => (
  <div data-testid="bag-crisis-intro" className="rounded-2xl border border-red-200 bg-red-50/60 p-4">
    <div className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
        <Warning size={18} weight="fill" />
      </span>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-red-700">
          Immediate help
        </div>
        <p className="mt-1 text-sm font-semibold leading-snug text-ink-900">
          Begin Again Living Ministries and this guide are not emergency,
          crisis, or medical services.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-ink-700">
          If you or someone with you is in immediate danger, please contact
          emergency services right now. To share the most relevant help, are
          you currently in the United States?
        </p>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      <button
        type="button"
        onClick={onUS}
        data-testid="bag-crisis-yes-us"
        className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
      >
        Yes, I'm in the U.S.
      </button>
      <button
        type="button"
        onClick={onIntl}
        data-testid="bag-crisis-no-us"
        className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
      >
        No / outside the U.S.
      </button>
    </div>
  </div>
);

const CrisisUSView = () => (
  <div data-testid="bag-crisis-us" className="rounded-2xl border border-red-200 bg-red-50/60 p-4">
    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-red-700">
      United States — immediate help
    </div>
    <ul className="mt-3 space-y-3 text-sm leading-relaxed text-ink-800">
      <li>
        <span className="font-semibold text-red-700">Call 911</span> for
        immediate medical or safety emergencies.
      </li>
      <li>
        <span className="font-semibold text-red-700">988</span> — call or text
        the Suicide &amp; Crisis Lifeline for free, confidential support 24/7.
      </li>
      <li>
        Text{" "}
        <span className="font-semibold text-red-700">HOME to 741741</span> to
        reach the Crisis Text Line.
      </li>
    </ul>
    <div className="mt-4 flex flex-wrap gap-2">
      <a
        href="tel:911"
        data-testid="bag-crisis-call-911"
        className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
      >
        Call 911
      </a>
      <a
        href="tel:988"
        data-testid="bag-crisis-call-988"
        className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
      >
        Call 988
      </a>
      <a
        href="sms:741741&body=HOME"
        data-testid="bag-crisis-text-741741"
        className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
      >
        Text 741741
      </a>
    </div>
    <p className="mt-4 text-xs leading-snug text-ink-600">
      You can also reach out to our team when you are safe. This guide will not
      attempt crisis counseling.
    </p>
  </div>
);

const CrisisIntlView = () => (
  <div data-testid="bag-crisis-intl" className="rounded-2xl border border-red-200 bg-red-50/60 p-4">
    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-red-700">
      Immediate help — outside the U.S.
    </div>
    <p className="mt-3 text-sm leading-relaxed text-ink-800">
      Please contact your local emergency number right now, or reach a
      crisis helpline in your country. An international directory is available
      at Find A Helpline.
    </p>
    <div className="mt-4 flex flex-wrap gap-2">
      <a
        href="https://findahelpline.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="bag-crisis-findahelpline"
        className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
      >
        Open Find A Helpline
        <ArrowUpRight size={12} weight="bold" />
      </a>
    </div>
    <p className="mt-4 text-xs leading-snug text-ink-600">
      You can also reach out to our team when you are safe. This guide will not
      attempt crisis counseling.
    </p>
  </div>
);

export default BeginAgainGuide;
